import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ArrowTopRightOnSquareIcon, DocumentTextIcon, InformationCircleIcon, ShieldCheckIcon } from '@heroicons/react/24/outline';
import BaseDialog from './BaseDialog';
import { logoutUser } from '../slices/authSlice';
import { acceptLegal, fetchPendingLegal, formatEffectiveDate, LEGAL_PATHS } from '../utils/legal';

const noop = () => {};
const ICONS = { terms: DocumentTextIcon, privacy: ShieldCheckIcon };

function agreementLabel(pending) {
  const terms = pending.some((d) => d.document === 'terms');
  const privacy = pending.some((d) => d.document === 'privacy');
  if (terms && privacy) return 'I agree to the Terms of Use and have read the Privacy Policy';
  return terms ? 'I agree to the Terms of Use' : 'I have read the Privacy Policy';
}

// Blocks the dashboard until a forecaster accepts the current Terms of Use and
// Privacy Policy. The backend decides who is asked (the pending list is always
// empty for other roles), and any error renders nothing, so this can never
// lock anyone out. The API itself is never gated.
export default function LegalUpdateDialog() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [pending, setPending] = useState([]);
  const [agreed, setAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(
    () => fetchPendingLegal().then(setPending).catch(() => setPending([])),
    []
  );
  useEffect(() => { load(); }, [load]);

  if (pending.length === 0) return null;

  const title = pending.length > 1
    ? 'Updated Terms of Use and Privacy Policy'
    : `Updated ${pending[0].title}`;

  const accept = async () => {
    setSubmitting(true);
    try {
      const versions = Object.fromEntries(pending.map((d) => [d.document, d.version]));
      setPending(await acceptLegal(versions));
    } catch (err) {
      // 409: the texts changed since we loaded them; show the new ones.
      if (err?.response?.status === 409) await load();
      else toast.error('Could not save your acceptance. Please try again.');
    } finally {
      setAgreed(false);
      setSubmitting(false);
    }
  };

  const signOut = () => {
    dispatch(logoutUser());
    navigate('/', { replace: true });
  };

  return (
    <BaseDialog
      open
      onClose={noop}
      showClose={false}
      title={title}
      titleAs="h2"
      zIndexClass="z-[300]"
      maxWidthClass="max-w-2xl"
    >
      <div className="px-6 pb-6 pt-2 text-sm text-gray-700 dark:text-gray-300">
        <p className="text-gray-500 dark:text-gray-400">
          Review and accept to continue to your dashboard.
        </p>

        <ul className="mt-4 divide-y divide-gray-200/70 rounded-xl border border-gray-200/70 dark:divide-gray-600/70 dark:border-gray-600/70">
          {pending.map((d) => {
            const Icon = ICONS[d.document] || DocumentTextIcon;
            return (
              <li key={d.document} className="flex gap-3 p-4">
                <span className="mt-0.5 flex h-8 w-8 flex-none items-center justify-center rounded-full bg-brand-50 text-brand-600 dark:bg-brand-900/30 dark:text-brand-400">
                  <Icon className="h-4 w-4" aria-hidden="true" />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-gray-900 dark:text-gray-100">{d.title}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Effective {formatEffectiveDate(d.version)}</p>
                  <p className="mt-2 leading-relaxed">{d.summary}</p>
                  <a
                    href={LEGAL_PATHS[d.document]}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-2 inline-flex items-center gap-1 font-medium text-brand-600 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 dark:text-brand-400"
                  >
                    Read the {d.title}
                    <ArrowTopRightOnSquareIcon className="h-4 w-4" aria-hidden="true" />
                  </a>
                </div>
              </li>
            );
          })}
        </ul>

        <div className="mt-4 flex gap-2 rounded-lg bg-gray-50 px-3 py-2.5 text-xs leading-relaxed text-gray-600 dark:bg-gray-800 dark:text-gray-300">
          <InformationCircleIcon className="mt-px h-4 w-4 flex-none text-gray-400 dark:text-gray-500" aria-hidden="true" />
          <p>
            Your API submissions are not interrupted. Under the Terms, continuing to use Predico
            after the effective date counts as acceptance; confirming it here records your
            acceptance, which is needed before payments can be processed.
          </p>
        </div>

        <label className="mt-5 flex items-start gap-3">
          <input
            type="checkbox"
            checked={agreed}
            onChange={(e) => setAgreed(e.target.checked)}
            className="mt-0.5 h-4 w-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500 dark:border-gray-600 dark:bg-gray-800"
          />
          <span className="text-gray-900 dark:text-gray-100">{agreementLabel(pending)}</span>
        </label>

        <div className="mt-5 flex flex-col-reverse gap-2 border-t border-slate-900/10 pt-4 sm:flex-row sm:justify-end dark:border-gray-100/10">
          <button
            type="button"
            onClick={signOut}
            className="rounded-md bg-white px-4 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 dark:bg-gray-800 dark:text-gray-100 dark:ring-gray-600 dark:hover:bg-gray-600"
          >
            Sign out
          </button>
          <button
            type="button"
            onClick={accept}
            disabled={!agreed || submitting}
            className="rounded-md border border-brand-500 bg-brand-50 px-4 py-2 text-sm font-medium text-brand-700 transition-colors hover:bg-brand-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-brand-900/30 dark:text-brand-300 dark:hover:bg-brand-900/50"
          >
            Accept and continue
          </button>
        </div>
      </div>
    </BaseDialog>
  );
}
